
import { useState } from 'react';
import { useEffect } from 'react';
import React from 'react';
import { ArrowsClockwiseIcon, ArrowsCounterClockwiseIcon, CellSignalFullIcon, CellSignalMediumIcon, CellSignalXIcon, ChartLineIcon, PencilSimpleIcon, TrashSimpleIcon, UserIcon, WalletIcon } from '@phosphor-icons/react'
import { Popover, Pagination, Modal } from 'antd';
import TransactionHistory from './TransactionHistory';
import AccountEditModal from './AccountEditModal';
import StatsCard from 'components/BasicStatsCard';
import { useTheme } from 'components/provider/theme';
import { API_URL } from 'env';
import { TradeAccount, useData } from 'components/provider/data';

interface Staff {
    name: string;
    id: string;
    email: string;
    avatar: string;
}

interface AccountInfo {
    account: {
        name: string;
        id: string;
        email: string;
        avatar: string;
        link: string;
    },
    platform: {
        name: string;
        version: string;
        link: string;
        id: string;
    },
    balance: number;
    equity: number;
    pl: number;
    staff: Staff;
    status: 'Active' | 'Pending' | 'Inactive';
}
export type { AccountInfo };
const ActiveTradingAccountList: React.FC = () => {
    const [resetCounter, setResetCounter] = useState(0);
    const [resetTimer, setResetTimer] = useState<NodeJS.Timeout | null>(null);
    const [data, setData] = useState<AccountInfo[]>([]);
    const [page, setPage] = useState(1);
    const { theme } = useTheme()
    const { setTradeAccounts } = useData();

    async function fetchData() {
        try {
            const response = await fetch(`${API_URL}api/v1/be/trade_accounts`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            const accounts = data.data.items.map((item: any) => ({
                account: {
                    name: item.user_display?.name || item.user?.fullname || item.nickname || 'Unknown Name',
                    id: item.id,
                    email: item.user_display?.email || item.user?.email || 'No Email Provided',
                    avatar: item.user_display?.avatar || item.user?.avatar || '',
                    link: `/account/${item.id}`
                },
                platform: {
                    name: item.platform || 'Unknown Platform',
                    version: item.platform_version || 'Unknown Version',
                    link: item.platform_link || '#',
                    id: item.account_number || '#'
                },
                balance: item.balance ?? 0,
                equity: item.equity ?? 0,
                pl: item.pl ?? 0,
                staff: {
                    name: item.user?.fullname || 'No User Assigned',
                    id: item.user?.id || 'N/A',
                    avatar: item.user?.avatar_url || '',
                    email: item.user?.email || 'No Email Provided'
                },
                status: item.status || 'Inactive'
            }));
            setData(accounts);
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
    }
    // Randomize data every 1 minute
    useEffect(() => {
        fetchData();
        const timer = setInterval(() => {
            fetchData();
        }, 60000);
        setResetTimer(timer);
        return () => {
            if (resetTimer) clearInterval(resetTimer);
        };
    }, []);
    // Reset data when resetCounter changes
    useEffect(() => {
        if (resetCounter > 0) {
            fetchData();
        }
    }, [resetCounter]);

    return (
        <div className={`w-full py-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white ring-1 ring-gray-100'} rounded-lg shadow-lg `}>
            <div className="flex flex-row items-center justify-between px-4 py-2">
                <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}  mb-4`}>Active Trading Accounts</h2>
                <div className="flex flex-row gap-4">
                    <ArrowsClockwiseIcon
                        className={`cursor-pointer text-gray-500 hover:text-gray-700 transition-colors duration-300 ${resetCounter > 0 ? 'animate-spin' : ''}`}
                        onClick={() => {
                            setResetCounter(prev => prev + 1);
                            // Remove spin after animation (1s)
                            setTimeout(() => setResetCounter(0), 1000);
                        }}
                        size={22}
                    />
                    <p>
                        <span className="text-gray-500 text-sm">Last Updated: {new Date().toLocaleTimeString()}</span>
                    </p>
                </div>
            </div>
            <div>
                {/* filter go here */}
            </div>
            <div className="overflow-x-auto" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
                <table className="w-full min-w-[800px] overflow-scroll">
                    <thead>
                        <tr className={` ${theme === 'dark' ? 'text-white' : 'bg-gray-100 text-[#6B7280] border-gray-200'}   `}
                            style={{
                                borderBottom: theme === 'dark' ? '' : '1px solid #e0e0e0',
                                borderTop: theme === 'dark' ? '' : '1px solid #e0e0e0'
                            }}
                        >
                            <th className="text-center p-2 font-light w-1/8">Account</th>
                            <th className="text-left p-2 font-light w-1/8">Platform</th>
                            <th className="text-left p-2 font-light w-1/8">Balance</th>
                            <th className="text-left p-2 font-light w-1/8">Equity</th>
                            <th className="text-left p-2 font-light w-1/8">P&L</th>
                            <th className="text-left p-2 font-light w-1/8">User</th>
                            <th className="text-left p-2 font-light w-1/8">Status</th>
                            <th className="text-left p-2 font-light w-1/8">Actions</th>

                        </tr>
                    </thead>
                    <tbody>
                        {
                            data.slice(
                                (page - 1) * 10,
                                page * 10
                            ).map((item, index) => (
                                <AccountItems key={index} account={item} />
                            ))
                        }
                    </tbody>
                </table>
                <div className="!pt-4  px-8 flex flex-row !w-full justify-between items-center" >
                    <p className="text-sm text-gray-500 text-center">
                        Showing {data.length} accounts
                    </p>
                    <Pagination align="center" defaultCurrent={page} total={data.length} onChange={
                        (page) => setPage(page)
                    } />
                </div>
            </div>
        </div>
    )
}
export default ActiveTradingAccountList;

const AccountItems = ({ account }: { account: AccountInfo }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [active, setActive] = useState<boolean | undefined>();
    const [balance, setBalance] = useState(account.balance);
    const [equity, setEquity] = useState(account.equity);
    const [pl, setPl] = useState(account.pl);
    const { theme } = useTheme()
    const { total_balance, setTotalBalance  , setTradeAccounts} = useData();

    async function fetchUserAccount() {
        try {
            const response = await fetch(`${API_URL}api/v1/be/trade_account/${account.platform.id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                }
            });
            if (!response.ok) {
                setActive(false);
            }
            else {
                const data = await response.json();
                setBalance(data.data.balance || 0);
                setEquity(data.data.equity || 0);
                setPl(data.data.profit || 0);
                setActive(true);
                setTotalBalance(prev => [...prev, { account: account.account.id, name: account.account.name, total: Number(data.data.balance) || 0 }]);
                const tradeAccount: TradeAccount = {
                    id: account.account.id,
                    login_id: account.platform.id,
                    nickname: account.account.name,
                    platform: account.platform.name,
                    broker: account.platform.version,
                    balance: Number(data.data.balance) || 0,
                    equity: Number(data.data.equity) || 0,
                    pl: Number(data.data.profit) || 0,
                    linked_user: {
                        id: account.staff.id,
                        name: account.staff.name,
                        email: account.staff.email
                    }
                }
                setTradeAccounts(prev => [...prev, tradeAccount]);
            }
        } catch (error) {
            console.error('Error fetching user account:', error);
            return null;
        }
    }
    useEffect(() => {
        setTotalBalance([])
       // fetchUserAccount();
    }, [account]);
    return (
        <>
            <Modal
                title="Edit Account"
                centered
                open={editModalOpen}
                footer={null}
                onCancel={() => setEditModalOpen(false)}
                width={800}
            >
                <AccountEditModal account={account.account.id} onClose={() => setEditModalOpen(false)} />
            </Modal>
            <Modal
                title="Details"
                centered
                open={modalOpen}
                footer={null}
                onCancel={() => setModalOpen(false)}
                width={1000}
            >
                <div className="flex flex-col items-center justify-center h-[80vh] overflow-y-auto">

                    <div className="w-full mt-4 h-full">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <StatsCard
                                title="Balance"
                                value={`$${balance.toLocaleString()}`}
                                icons={<WalletIcon size={24} />}
                                iconBackgroundColor="#E0F2FE"
                            />
                            <StatsCard
                                title="Equity"
                                value={`$${equity.toLocaleString()}`}
                                icons={<ArrowsCounterClockwiseIcon size={24} />}
                                iconBackgroundColor="#E0F2FE"
                            />
                            <StatsCard
                                title="P&L"
                                value={`$${pl.toLocaleString()}`}
                                icons={<CellSignalFullIcon size={24} />}
                                iconBackgroundColor={pl >= 0 ? "#D1FAE5" : "#FEE2E2"}
                            />

                        </div>
                        <TransactionHistory
                            option={
                                {
                                    accountLoginId: account.platform.id,
                                    platform: account.platform.name,
                                    accountName: account.account.name,
                                    title: false,
                                    table: true,
                                    filter: true,
                                    pagination: true,
                                    randomize: true,
                                    randomizeInterval: 1000,
                                    border: false,
                                    shadow: false,
                                    padding: {
                                        top: '20px',
                                        bottom: '20px',
                                        left: '0',
                                        right: '0'
                                    },
                                    colWidth: ['w-[10%]', 'w-[10%]', 'w-[10%]', 'w-[10%]', 'w-[10%]', 'w-[10%]', 'w-[10%]', 'w-[20%]', 'w-[10%]']
                                }
                            }
                        />
                    </div>
                </div>
            </Modal>

            <tr className={`${theme === 'dark' ? 'border-b border-gray-700 hover:bg-gray-700' : 'border-b border-gray-200 hover:bg-gray-50'} `}>
                <td className="p-2">
                    <div className="flex items-center gap-2 px-4">
                        <div>
                            <a href={account.account.link} className={`${theme === 'dark' ? 'text-blue-600' : 'text-blue-500'} hover:underline`}>{account.account.name}</a>
                            <p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-500'}  !m-0`}>ID: {account.account.id}</p>
                        </div>
                    </div>
                </td>
                <td className="p-2">
                    <div className="flex flex-col items-start gap-2">
                        <div className="rounded-[10px] bg-gray-200 flex items-center justify-center mr-2">
                            <p className={`text-xs text-gray-700 !m-0 px-2 py-1`}>
                                {account.platform.name}
                            </p>
                        </div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-500'}  !m-0`}>{account.platform.id}</p>
                    </div>
                </td>
                <td className={`p-2 text-sm ${theme === 'dark' ? 'text-white' : ''}`}>${balance.toLocaleString()}</td>
                <td className={`p-2 text-sm ${theme === 'dark' ? 'text-white' : ''}`}>${equity.toLocaleString()}</td>
                <td className={`p-2 text-sm ${pl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {pl > 0 ? '+' : pl < 0 ? '-' : ''}
                    ${Math.abs(pl).toLocaleString()}
                </td>
                <td className="p-2 text-sm items-center justify-center">
                    <Popover
                        content={
                            <div className="flex items-center gap-2 cursor-pointer">
                                <div className="w-8 h-8 text-white flex-shrink-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                                    <UserIcon size={24} />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-700 !m-0">{account.staff.name}</p>
                                    <p className="text-xs text-gray-500 !m-0">{account.staff.email}</p>
                                </div>
                            </div>
                        }
                        title="User Info"
                        align={{ offset: [-50, -10] }}
                        arrow={false}
                    >
                        <p className="text-sm text-blue-500 hover:underline cursor-pointer !m-0">
                            <a href={`/users/${account.staff.id}`} className="text-blue-500 hover:underline">
                                {account.staff.name}
                            </a>
                        </p>
                    </Popover>
                </td>
                <td className={` p-2 `}>
                    <div className={` items-center text-center px-2 py-1 rounded-full text-xs font-semibold ${active
                        ? 'bg-green-100 text-green-800'
                        : !active
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                        {active === true ? 'Active' : active === false ? 'Inactive' : 'Unknown'}
                    </div>
                    <div className={`text-xs  flex flex-row gap-2 text-gray-500 m-0 ${active === true
                        ? 'text-green-500'
                        : active === false
                            ? 'text-red-500'
                            : 'text-yellow-500'
                        }`}>
                        {active === true ? (
                            <>
                                <CellSignalFullIcon size={20} /> Online
                            </>
                        ) : active === false ? (
                            <>
                                <CellSignalXIcon size={20} /> Offline
                            </>
                        ) : (
                            <>
                                <CellSignalMediumIcon size={20} /> Unknown
                            </>
                        )}
                    </div>
                </td>
                <td className="p-2 text-sm">
                    <div className="flex flex-row gap-2 w-full items-center justify-center">
                        <p
                            onClick={() => setModalOpen(true)}
                            className="cursor-pointer p-2 rounded-[5px] m-0 hover:bg-[#E0E0E0] transition-all duration-500">
                            <ChartLineIcon size={18} fill={theme === 'dark' ? '#fff' : ''} />
                        </p>
                        <p
                            onClick={() => setEditModalOpen(true)}
                            className="cursor-pointer p-2 rounded-[5px] m-0 hover:text-blue-700 hover:bg-blue-200 transition-all duration-500">
                            <PencilSimpleIcon size={18} fill={theme === 'dark' ? '#fff' : ''} />
                        </p>
                        <p className=" cursor-pointer p-2 rounded-[5px] m-0 text-red-700 hover:bg-red-200 transition-all duration-500">
                            <TrashSimpleIcon size={18} fill={theme === 'dark' ? '#fff' : ''} />
                        </p>
                    </div>
                </td>
            </tr>
        </>

    )
}